import { DocumentEmptyIcon } from '@adsk/alloy-react-icon';
import VirtualizedTree from '@adsk/nirvana-virtualized-tree';

import Services, {
  FileUtil,
  FolderUtil,
  cancelRequests,
  CANCEL_KEYS,
  SortingUtil,
} from './sdkServices';
import { isMalwareDetected } from './utils';
import {
  Folder,
  FolderDistributed,
  FolderDistributedNSEnforced,
  FolderNSEnforced,
} from '../assets';

export const INNER_NODES = {
  LOADING: VirtualizedTree.NODE_KEYS.NODE_LOADER_KEY,
  LOAD_ERROR: VirtualizedTree.NODE_KEYS.NODE_ERROR_KEY,
  LOAD_MORE: Symbol('LOAD_MORE'),
};

export const BOOTSTRAP_NODE = {
  id: '__BOOTSTRAP_NODE__',
  type: Symbol('BOOTSTRAP_NODE'),
};

export const isInitializingTree = (entities) => {
  return entities[0]?.type === BOOTSTRAP_NODE.type;
};

export const isCancelError = (error) => !!error.__CANCEL__;

const getContentRequestMap = new Map();

export const getEntityIcon = (entity) => {
  const isFolder = FileUtil.isFolder(entity);

  if (isFolder) {
    if (FolderUtil.isSharedFolder(entity)) {
      return entity.hasNS ? FolderDistributedNSEnforced : FolderDistributed;
    } else {
      return entity.hasNS ? FolderNSEnforced : Folder;
    }
  }

  return DocumentEmptyIcon;
};

const getLoadingNode = (node) => ({
  [VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]: INNER_NODES.LOADING,
  urn: node.urn,
  path: node.path,
  fullPath: node.fullPath,
});

const getLoadMoreNode = (node) => ({
  [VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]: INNER_NODES.LOAD_MORE,
  urn: node.urn,
  path: node.path,
  fullPath: node.fullPath,
});

const getRetryNode = (node) => ({
  [VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]: INNER_NODES.LOAD_ERROR,
  urn: node.urn,
  path: node.path,
  parentNode: node,
  fullPath: node.fullPath,
});

export const genEntityDisplayLabel = (entity) => {
  return FileUtil.isFolder(entity)
    ? FolderUtil.getFolderTitle(entity)
    : entity.name;
};

// always expendable for folder
export const isNodeExpandable = (node) => FileUtil.isFolder(node);

export const isBuiltInNode = (node) =>
  !!node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE];

export const isNodeSelectable = (node, isMixMode) => {
  if (node.readonly) return false;

  if (isBuiltInNode(node)) return false; // excludes loading/loadMore/loadError

  if (isMixMode) return true; // both folder/files can select

  return !FileUtil.isFolder(node); // only file can select
};

const getTargetFolder = (nodes = [], key, childrenKey = 'folders') => {
  let target;
  nodes.some((node) => {
    if (node.urn === key) {
      target = node;
      return true;
    }

    const subFolder = getTargetFolder(node[childrenKey], key, childrenKey);
    if (subFolder) {
      target = subFolder;
      return true;
    }
    return false;
  });

  return target;
};

// attach path to files like folder
export const getFileUrnPath = (parentUrnPath, urn) => `${parentUrnPath}/${urn}`;

// attach idPath to file&folder
export const getIdPath = (parentIdPath, id) => `${parentIdPath}/${id}`;

/****** Sort *******/
const folderTypeOrder = ({ folderType }) => {
  switch (folderType) {
    case 'plan':
      return 1;
    case 'drawing':
      return 2;
    case 'shared':
      return 3;
    default:
      return 4;
  }
};

const folderSorter = (folderA, folderB) => {
  // Sort folder type first to ensure Distributed folder is ahead of Project Files folder
  const aOrder = folderTypeOrder(folderA);
  const bOrder = folderTypeOrder(folderB);
  if (aOrder > bOrder) return 1;
  if (aOrder < bOrder) return -1;

  // Natural sort folder title
  const rank = SortingUtil.naturalSort(folderA.title, folderB.title);
  if (rank > 0) return 1;
  if (rank < 0) return -1;
  return 0;
};

export const fileSorter = (fileA, fileB) => {
  // Natural sort folder title
  const rank = SortingUtil.naturalSort(fileA.name, fileB.name);
  if (rank > 0) return 1;
  if (rank < 0) return -1;
  return 0;
};

const recursiveSort = (folders, sorter, needRecursive) => {
  const sorted = [...folders].sort(sorter);
  if (needRecursive) {
    sorted.forEach((folder) => {
      if (Array.isArray(folder.folders) && folder.folders.length > 0) {
        folder.folders = recursiveSort(folder.folders, sorter, needRecursive);
      }
    });
  }
  return sorted;
};

export const sortFolders = (folders, needRecursive = true) => {
  return recursiveSort(folders, folderSorter, needRecursive);
};

export const sortFiles = (files) => [...files].sort(fileSorter);

export const attachFullPath2TopFolders = (folders, parentFullPath = '') => {
  folders.forEach((folder) => {
    folder.fullPath = parentFullPath
      ? `${parentFullPath}/${folder.title}`
      : folder.title;

    if (folder.folders.length > 0) {
      attachFullPath2TopFolders(folder.folders, folder.fullPath);
    }
  });
};

export const sortEntities = (entities, needRecursive = true) => {
  const folders = entities.filter((entity) => 'folderType' in entity);
  const files = entities.filter((entity) => 'entityType' in entity);
  const sortedFolders = sortFolders(folders, needRecursive);
  const sortedFiles = sortFiles(files);

  return [...sortedFolders, ...sortedFiles];
};

/***** Tree CRUD operations *******/
export const addNodes = (nodes, node, newNodes, childrenKey = 'folders') => {
  const targets = [...nodes];
  const targetNode = getTargetFolder(targets, node.urn, childrenKey);
  // filter out the action node like loading more / loading spinner / loading error
  if (targetNode) {
    const curChildren = targetNode[childrenKey] || [];
    // attach path to file like folder
    // attach idPath to folder&file
    newNodes = newNodes.map((target) => {
      if ('entityType' in target) {
        return {
          ...target,
          path: getFileUrnPath(node.path, target.urn),
          idPath: getIdPath(node.idPath, target.id),
        };
      }
      return {
        ...target,
        idPath: getIdPath(node.idPath, target.id),
      };
    });

    const sortedNodes = sortEntities([...curChildren, ...newNodes], false);
    // only the last one can be builtIn node which has already ignored in sortEntities
    const lastNode = newNodes.length > 0 && newNodes[newNodes.length - 1];
    targetNode[childrenKey] =
      lastNode && isBuiltInNode(lastNode)
        ? [...sortedNodes, lastNode]
        : sortedNodes;
  }
  return targets;
};

export const addLoadingNode = (nodes, urn) => {
  nodes = [...nodes];
  const targetFolder = getTargetFolder(nodes, urn);

  if (targetFolder) {
    targetFolder.folders = targetFolder.folders
      .filter(
        (node) =>
          ![
            INNER_NODES.LOAD_MORE,
            INNER_NODES.LOADING,
            INNER_NODES.LOAD_ERROR,
          ].includes(node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]),
      )
      .concat(getLoadingNode(targetFolder));
  }

  return nodes;
};

export const addRetryNode = (nodes, urn) => {
  nodes = [...nodes];
  const targetFolder = getTargetFolder(nodes, urn);

  if (targetFolder) {
    targetFolder.folders = targetFolder.folders
      .filter(
        (node) =>
          ![
            INNER_NODES.LOAD_MORE,
            INNER_NODES.LOADING,
            INNER_NODES.LOAD_ERROR,
          ].includes(node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]),
      )
      .concat(getRetryNode(targetFolder));
  }

  return nodes;
};

export const updateNode = (nodes, urn, data, childrenKey = 'folders') => {
  const targets = [...nodes];
  const targetNode = getTargetFolder(targets, urn, childrenKey);
  if (targetNode) {
    Object.assign(targetNode, data);
    return targets;
  }

  return undefined;
};

export const clearNodes = (nodes, urn, childrenKey = 'folders') => {
  const targetNode = getTargetFolder(nodes, urn, childrenKey);
  if (targetNode) {
    targetNode[childrenKey] = (targetNode[childrenKey] || []).filter((node) =>
      FileUtil.isFolder(node),
    );
  }
};

export const cleanFolderContent = (nodes, urn) => {
  const targets = [...nodes];
  const targetNode = getTargetFolder(targets, urn);
  if (targetNode) {
    targetNode.folders = [];
  }

  return targets;
};

export const cleanFolderPagination = (
  paginationMap,
  targetUrn,
  targetUrnPath,
) => {
  const pagination = paginationMap.get(targetUrn);
  if (pagination) {
    [...paginationMap.keys()].forEach((key) => {
      const { path } = paginationMap.get(key);
      if (path.includes(targetUrnPath)) {
        paginationMap.delete(key);
      }
    });
  }

  return paginationMap;
};

export const filterNodes = (nodes, childrenKey = 'folders', filter) => {
  if (!filter || typeof filter !== 'function') {
    return nodes;
  }

  const targets = [...nodes].filter(filter);
  targets.forEach((target) => {
    if (Array.isArray(target[childrenKey]) && target[childrenKey].length > 0) {
      target[childrenKey] = filterNodes(
        target[childrenKey],
        childrenKey,
        filter,
      );
    }
  });
  return targets;
};

const getPagination = (pagination) => {
  const { limit, offset, nextUrl, hasNext } = pagination;
  const nextOffset = offset + limit;
  const nextLimit = limit;

  return {
    ...pagination,
    limit: nextLimit,
    offset: nextOffset,
    hasNext: !!(nextUrl || hasNext),
  };
};

export const fetchFolderPermission = async (projectId, folderUrn) => {
  try {
    return await Services.Folders.getFolderPermission(projectId, folderUrn);
  } catch {
    return [];
  }
};

// get sub folders and files.
export const getFolderContents = async (
  projectId,
  node,
  pagination,
  permissionActions = null,
) => {
  const { urn: folderUrn } = node;
  const { folder: folderPage, file: filePage } = pagination;
  let folders = [];
  let files = [];
  let nextFolderPage = folderPage;
  let nextFilePage = filePage;
  const shouldLoadFolders = !folderPage.isLoadedAll;
  let shouldLoadFiles = !filePage.isLoadedAll;
  const requestConf = {
    [CANCEL_KEYS]: {
      getter: () => getContentRequestMap.get(folderUrn),
      setter: (token) => getContentRequestMap.set(folderUrn, token),
      clearer: () => getContentRequestMap.delete(folderUrn),
    },
  };

  if (shouldLoadFolders) {
    const { folders: data, pagination } =
      await Services.Folders.getFoldersByFilter(
        projectId,
        folderUrn,
        {
          limit: folderPage.limit,
          offset: folderPage.offset,
          sort: [
            {
              name: 'name',
              asc: true,
            },
          ],
        },
        requestConf,
      );
    folders = data;
    folders = folders.map((folder) => ({
      ...folder,
      id: folder.urn,
      fullPath: `${node.fullPath}/${folder.title}`,
    }));
    nextFolderPage = getPagination(pagination);

    if (pagination.nextUrl) {
      folders.push(getLoadMoreNode(node));
      shouldLoadFiles = false;
    } else {
      nextFolderPage.isLoadedAll = true;
      shouldLoadFiles = true; // load files immediately when all folders loaded
    }
  }

  if (shouldLoadFiles) {
    const { documents, pagination } = await Services.Documents.getDocuments(
      projectId,
      folderUrn,
      {
        limit: filePage.limit,
        offset: filePage.offset,
        filterCriteria: [
          { name: 'entity_type', value: 'CONTAINER' },
          { name: 'entity_type', value: 'SEED_FILE' },
        ],
        sortCriteria: [
          {
            name: 'name',
            isCustomAttribute: false,
            asc: true,
          },
        ],
      },
      requestConf,
    );
    files = documents;
    files = files.map((file) => ({
      ...file,
      id: file.urn,
      fullPath: `${node.fullPath}/${file.name}`,
    }));
    if (permissionActions !== null) {
      files = files.map((file) => ({
        ...file,
        parentFolder: {
          ...file.parentFolder,
          permissionActions: permissionActions.map((a) => a.toUpperCase()),
        },
      }));
    }
    nextFilePage = getPagination(pagination);
    pagination.hasNext
      ? files.push(getLoadMoreNode(node))
      : (nextFilePage.isLoadedAll = true);
  }

  return {
    folders,
    files,
    nextFolderPage,
    nextFilePage,
    folderUrn,
  };
};

export const cancelFolderRequest = (urn) => {
  const token = getContentRequestMap.get(urn);
  token && cancelRequests([token]);
};

export const getImplicitSelectedKeys = (nodes, selectedKeys, valueKey) => {
  const implicitSelectedKeys = [];

  const _traverseTree = (nodes) => {
    nodes.forEach((node) => {
      if (isBuiltInNode(node)) return;
      if (isMalwareDetected(node)) return;

      const pathKey = valueKey === 'id' ? 'idPath' : 'path';
      const isChild = selectedKeys.some(
        (key) => node[pathKey].includes(key) && node[valueKey] !== key,
      );

      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator; O(n) vs O(n²) spread in a deep tree traversal
      if (isChild) implicitSelectedKeys.push(node[valueKey]);

      if (node.folders) _traverseTree(node.folders);
    });
  };

  _traverseTree(nodes);

  return implicitSelectedKeys;
};

export const customizeFileFilter = (files, dataFilter) => {
  if (files.length === 0) return [];

  const lastNode = files[files.length - 1];
  const builtInNode = isBuiltInNode(lastNode) ? lastNode : undefined;
  const realFiles = builtInNode ? files.slice(0, -1) : files;
  const filteredNodes = realFiles.length > 0 ? dataFilter(realFiles) : [];

  return builtInNode ? [...filteredNodes, builtInNode] : filteredNodes;
};

export const getNodeByKey = (nodes, key, valueKey) => {
  for (const node of nodes) {
    if (node[valueKey] === key) return node;
    if (!node.folders || node.folders.length === 0) continue;
    return getNodeByKey(node.folders || [], key, valueKey);
  }
  return undefined;
};

export const getNodesByKeys = (nodes, selectedKeys, valueKey) => {
  const selectedNodes = [];
  const _traverseNodes = (nodes) => {
    nodes.forEach((node) => {
      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator; O(n) vs O(n²) spread in a deep tree traversal
      if (selectedKeys.includes(node[valueKey])) selectedNodes.push(node);
      if (node.folders && node.folders.length > 0) _traverseNodes(node.folders);
    });
  };
  _traverseNodes(nodes);

  return selectedNodes;
};

const getDeselectedKeys = (selectedKeys, updatedKeys) => {
  const deselectedKeys = [];
  selectedKeys.forEach((target) => {
    if (!updatedKeys.includes(target)) {
      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator; O(n) vs O(n²) spread
      deselectedKeys.push(target);
    }
  });

  return deselectedKeys;
};

export const isFolderUrn = (urn) => urn.includes('fs.folder:co');

const purgeChildren = (nodes) => {
  const sortedNodes = nodes.toSorted((a, b) => {
    return a.idPath.localeCompare(b.idPath);
  });

  const topNodeMap = {};
  for (const node of sortedNodes) {
    if (
      !Object.keys(topNodeMap).some((key) => new RegExp(key).test(node.idPath))
    ) {
      topNodeMap[node.idPath] = node;
    }
  }

  return Object.values(topNodeMap);
};

// when enableSelectAll true, only return explicit nodes
// contrary to implicit nodes, explicit nodes EXCLUDES folder's sub folders and files
export const getExplicitNodes = (
  nodes,
  selectedKeys,
  updatedKeys,
  valueKey,
) => {
  const pathKey = valueKey === 'id' ? 'idPath' : 'path';
  let toSelectNodes = getNodesByKeys(nodes, updatedKeys, valueKey);
  // remove leaf nodes in toSelectNodes, only keep the top level folders&files
  toSelectNodes = purgeChildren(toSelectNodes);
  const deselectedKeys = getDeselectedKeys(selectedKeys, updatedKeys);
  // purge toSelectNodes sub-folders/files in deselectedKeys
  const explicitNodes = [];
  toSelectNodes.forEach((node) => {
    if (!deselectedKeys.find((key) => node[pathKey].includes(key))) {
      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator; O(n) vs O(n²) spread
      explicitNodes.push(node);
    }
  });

  return explicitNodes;
};

const mapId2Urn = (folders) => {
  folders.forEach((folder) => {
    folder.id = folder.urn;

    if (folder.folders.length > 0) {
      mapId2Urn(folder.folders);
    }
  });
};

const attachIdPath = (folders) => {
  const _traverseFolders = (folders, parentIdPath) => {
    folders.forEach((folder) => {
      folder.idPath = parentIdPath
        ? getIdPath(parentIdPath, folder.id)
        : folder.id;
      if (folder.folders && folder.folders.length > 0) {
        _traverseFolders(folder.folders, folder.idPath);
      }
    });
  };

  _traverseFolders(folders, undefined);
};

// Calculate and restructure folder tree with flat folders
const setupChild = (folders, folderMaps) => {
  folders.forEach((folder) => {
    if (folderMaps.has(folder.urn)) {
      const children = folderMaps.get(folder.urn);
      folder.folders = children;
      folder.hasSubfolders = folder.hasSubfolders || children.length > 0; // eslint-disable-line spellcheck/spell-checker
      children.length > 0 && setupChild(children, folderMaps);
    }
  });
};

// Rebuild the folders tree with flat folders
const restructureFolders = (data = []) => {
  const topFolders = [];
  const folderMap = new Map();

  data.forEach((folder) => {
    // If current folder has parents or is root, it's a top folder
    const parents = folder.parents || [];
    if (parents.length > 0 || folder.isRoot) {
      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator; O(n) vs O(n²) spread
      topFolders.push(folder);
    }

    const { parentUrn } = folder;

    if (!folderMap.has(parentUrn)) {
      folderMap.set(parentUrn, [folder]);
    } else {
      const children = folderMap.get(parentUrn);
      folderMap.set(parentUrn, [...children, folder]);
    }
  });

  const tree = [...topFolders];
  setupChild(tree, folderMap);
  return tree;
};

let parentRequestToken;
export const getTopFolders = async (projectId, folderUrn) => {
  let folders;
  if (folderUrn) {
    // Get parent folders and top folders
    const [{ results: parents }, topFolders] = await Promise.all([
      Services.Folders.getParentFolders(projectId, folderUrn, undefined, {
        [CANCEL_KEYS]: {
          getter: () => parentRequestToken,
          setter: (token) => (parentRequestToken = token),
          clearer: () => (parentRequestToken = undefined),
        },
      }).catch((err) => {
        const status = err.response?.status;
        // For specific status codes, return empty results to show top folders directly
        if (status === 404 || status === 403) return { results: [] };
        // For other status codes, throw the error to show retry page
        throw err;
      }),
      Services.Folders.getFolders(projectId),
    ]);

    // Parse and load sub folders for parent folders in path
    const folderUrns = parents
      .filter((folder) => folder.hasSubfolders) // eslint-disable-line spellcheck/spell-checker
      .map((folder) => folder.urn);
    const pathFolders = await Promise.all(
      folderUrns.map((urn) => Services.Folders.getFolder(projectId, urn)),
    );
    const flatFolders = pathFolders.flatMap((folder) => folder.folders || []);
    const flatUrnPaths = new Set(flatFolders.map((folder) => folder.urn));
    const topUrns = new Set(topFolders.map((folder) => folder.urn));
    // Clear all duplicate folders in parents
    const parentFolders = parents.filter(
      (parent) => !topUrns.has(parent.urn) && !flatUrnPaths.has(parent.urn),
    );
    folders = restructureFolders([
      ...topFolders,
      ...parentFolders,
      ...flatFolders,
    ]);
  } else {
    folders = await Services.Folders.getFolders(projectId);
  }
  folders = folders.filter((folder) =>
    ['normal', 'shared'].includes(folder.folderType),
  );

  mapId2Urn(folders);
  attachIdPath(folders);
  attachFullPath2TopFolders(folders);

  return folders;
};

export const getDefaultExpandKeys = (node) => {
  const expandKeys = [];
  const idPaths = node.idPath.split('/');
  const urnPaths = node.path.split('/');
  for (let i = 0; i < urnPaths.length; i++) {
    const expandKey = {
      id: idPaths[i],
      urn: urnPaths[i],
      path: urnPaths.slice(0, i + 1).join('/'),
      idPath: idPaths.slice(0, i + 1).join('/'),
    };
    expandKeys.push(expandKey);
  }

  return expandKeys;
};
